import { useEffect, useState } from "react";
import { FileText, ChevronLeft, ChevronRight } from "react-feather";
import type { ResourceResponse, PaginationInfo } from "../../types/resource";
import { resourceService } from "../../services/resource.service";
import { useSearchParams } from "react-router-dom";
import { ProgramDetails, programService } from "@/services/program.service";
import { useProgram } from "@/contexts/program";
import { Button } from "../ui/button";
import { SpinnerBlock } from "../ui/spinner";
import { EmptyState } from "../ui/empty-state";
import { usePortalScope } from "@/contexts/portalScope";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

// Function to process content and convert YouTube links to iframes (same as FAQ)
function processYouTubeContent(content: string): string {
  if (!content) return content;

  let processedContent = content.replace(
    /<a[^>]*href="(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+))"[^>]*>.*?<\/a>/g,
    (_, _url, videoId) => {
      const embedUrl = `https://www.youtube.com/embed/${videoId}`;
      return `<div class="youtube-embed-wrapper"><iframe width="100%" height="315" src="${embedUrl}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>`;
    }
  );

  // Then handle any plain YouTube URLs that might not be in links
  processedContent = processedContent.replace(
    /(?<!href=["'])https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)(?!["'])/g,
    (_, videoId) => {
      const embedUrl = `https://www.youtube.com/embed/${videoId}`;
      return `<div class="youtube-embed-wrapper"><iframe width="100%" height="315" src="${embedUrl}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>`;
    }
  );

  // Also wrap existing iframes in our wrapper for consistent styling
  processedContent = processedContent.replace(
    /<iframe([^>]*youtube[^>]*)><\/iframe>/g,
    '<div class="youtube-embed-wrapper"><iframe$1></iframe></div>'
  );

  return processedContent;
}

function formatDate(dateString: string): string {
  return resourceService.formatDate(dateString);
}

function ResourceCard({ resource }: { resource: ResourceResponse }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-card rounded-lg border border-border p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-brand-600" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {resource.title}
            </h3>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>•</span>
              <span>{formatDate(resource.createdAt)}</span>
            </div>
          </div>
        </div>
        {resource.content.length > 200 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-brand-600 hover:text-brand-700 text-sm font-medium"
          >
            {isExpanded ? "Show less" : "Read more"}
          </button>
        )}
      </div>

      <div className="relative">
        <div
          className={`prose max-w-none ${
            !isExpanded && resource.content.length > 200
              ? "max-h-32 overflow-hidden"
              : ""
          }`}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: processYouTubeContent(resource.content),
            }}
            className="text-muted-foreground"
          />
        </div>

        {/* Gradient overlay for collapsed content */}
        {!isExpanded && resource.content.length > 200 && (
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-card to-transparent pointer-events-none" />
        )}
      </div>
    </div>
  );
}

export function ResourceSharing() {
  const { activePrograms:programs } = useProgram();
  const [resources, setResources] = useState<ResourceResponse[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const { isMarketplaceHidden } = usePortalScope();
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [activePrograms, setActivePrograms] = useState<ProgramDetails[]>(programs);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    let cancelled = false;

    const fetchActivePrograms = async () => {
      // Prefer the context's list; fall back to our own fetch while it's
      // still loading. Never overwrite a fetched list with an empty context
      // snapshot — that race left the brand dropdown permanently empty
      // whenever this page mounted before the provider's fetch resolved.
      let effectivePrograms = programs;
      if (effectivePrograms.length === 0) {
        try {
          effectivePrograms = await programService.findActivePrograms();
        } catch (error) {
          console.error("Failed to fetch active programs:", error);
          effectivePrograms = [];
        }
      }
      if (cancelled) return;
      setActivePrograms(effectivePrograms);

      // Check if there's a selectedBrandId in the URL query parameters
      const urlBrandId = searchParams.get("selectedBrandId");
      if (urlBrandId) {
        // Verify that the brand ID exists in the active programs
        const brandExists = effectivePrograms.some(
          (program) => program.id === urlBrandId
        );
        if (brandExists) {
          setSelectedBrandId(urlBrandId);
          return;
        }
      }

      // The scoped portal hides the brand dropdown, so nothing else would ever
      // set a selection — without this the page sits on "select a brand" with
      // no control to do it with. Deliberately scoped-only: auto-selecting for
      // a marketplace affiliate who happens to have one brand would be a
      // behaviour change on the normal portal.
      if (isMarketplaceHidden && effectivePrograms.length > 0) {
        setSelectedBrandId(effectivePrograms[0].id);
      }
    };
    fetchActivePrograms();

    return () => {
      cancelled = true;
    };
  }, [searchParams, programs, isMarketplaceHidden]);

  useEffect(() => {
    const fetchResources = async () => {
      if (!selectedBrandId) return;
      setLoading(true);
      try {
        const response = await resourceService.getBrandResourcesForAffiliates(
          selectedBrandId,
          pagination.page,
          pagination.limit
        );
        setResources(response.data);
        setPagination(response.pagination);
      } catch (error) {
        console.error("Error fetching resources:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchResources();
  }, [selectedBrandId, pagination.page]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handleBrandSelection = (brandId: string) => {
    setSelectedBrandId(brandId);
    // Reset pagination when changing brand
    setPagination((prev) => ({ ...prev, page: 1 }));

    // Update URL query parameter
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("selectedBrandId", brandId);
    setSearchParams(newSearchParams);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-brand-600" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">Resources</h2>
        </div>
        {/* One brand in a scoped portal, and it is already selected — a
            dropdown with a single option is a control that cannot do
            anything. */}
        <div className={`w-full lg:w-64${isMarketplaceHidden ? " hidden" : ""}`}>
          <Select
            value={selectedBrandId ?? undefined}
            onValueChange={handleBrandSelection}
          >
            <SelectTrigger className="bg-card shadow-sm">
              <SelectValue placeholder="Select Brand" />
            </SelectTrigger>
            <SelectContent>
              {activePrograms.map((program) => (
                <SelectItem key={program.id} value={program.id}>
                  {program.companyName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <SpinnerBlock />
      ) : (
        <>
          <div className="space-y-6">
            {resources.length > 0 &&
              resources.map((resource) => (
                <ResourceCard key={resource.id} resource={resource} />
              ))}
          </div>

          {resources.length === 0 && selectedBrandId && (
            <EmptyState
              icon={<FileText />}
              title="No resources shared yet"
              body="The brand will share resources here when they become available"
            />
          )}

          {!selectedBrandId && (
            <EmptyState
              icon={<FileText />}
              title={
                isMarketplaceHidden
                  ? "No resources yet"
                  : "Select a brand to view resources"
              }
              body={
                isMarketplaceHidden
                  ? "Resources shared by your brand will show up here"
                  : "Choose a brand from the dropdown above to see their shared resources"
              }
            />
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && selectedBrandId && (
            <div className="flex items-center justify-between border-t border-border bg-card px-4 py-3 sm:px-6 mt-6">
              <div className="flex flex-1 justify-between sm:hidden">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-3"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next
                </Button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Showing{" "}
                    <span className="font-medium">
                      {(pagination.page - 1) * pagination.limit + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(
                        pagination.page * pagination.limit,
                        pagination.total
                      )}
                    </span>{" "}
                    of <span className="font-medium">{pagination.total}</span>{" "}
                    results
                  </p>
                </div>
                <div>
                  <nav
                    className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-muted-foreground ring-1 ring-inset ring-border hover:bg-accent focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                    </button>

                    {/* Page numbers */}
                    {Array.from(
                      { length: pagination.totalPages },
                      (_, i) => i + 1
                    )
                      .filter((page) => {
                        // Show first page, last page, current page, and pages around current
                        return (
                          page === 1 ||
                          page === pagination.totalPages ||
                          Math.abs(page - pagination.page) <= 1
                        );
                      })
                      .map((page, index, array) => {
                        // Add ellipsis if there's a gap
                        const showEllipsisBefore =
                          index > 0 && array[index - 1] !== page - 1;

                        return (
                          <div key={page} className="flex items-center">
                            {showEllipsisBefore && (
                              <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-muted-foreground ring-1 ring-inset ring-border">
                                ...
                              </span>
                            )}
                            <button
                              onClick={() => handlePageChange(page)}
                              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-border focus:z-20 focus:outline-offset-0 ${
                                page === pagination.page
                                  ? "z-10 bg-brand-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
                                  : "text-foreground hover:bg-accent"
                              }`}
                            >
                              {page}
                            </button>
                          </div>
                        );
                      })}

                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-muted-foreground ring-1 ring-inset ring-border hover:bg-accent focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* YouTube Embed Styles */}
      <style>{`
        .youtube-embed-wrapper {
          position: relative;
          width: 100%;
          padding-bottom: 56.25%; /* 16:9 aspect ratio */
          height: 0;
          margin: 16px 0 !important;
          overflow: hidden;
          border-radius: 8px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
        
        .youtube-embed-wrapper iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100% !important;
          height: 100% !important;
          border: none !important;
          border-radius: 8px;
        }
        
        /* Ensure YouTube embeds are properly contained within the prose container */
        .prose .youtube-embed-wrapper {
          margin: 16px 0 !important;
          max-width: 100% !important;
        }
        
        /* Handle collapsed state better */
        .prose.max-h-32 {
          position: relative;
        }
        
        .prose.max-h-32 .youtube-embed-wrapper {
          margin: 8px 0 !important;
        }
        
        /* Ensure images are also properly sized */
        .prose img {
          max-width: 100% !important;
          height: auto !important;
          border-radius: 8px;
          margin: 8px 0;
        }
        
        @media (max-width: 640px) {
          .youtube-embed-wrapper {
            padding-bottom: 65%; /* Slightly taller on mobile for better viewing */
            margin: 8px 0 !important;
          }
        }
      `}</style>
    </div>
  );
}
